import {useEffect, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';
import EventBanner from './EventBanner';
import ViewerEnd from './ViewerEnd';
import IMG_LEFT from '../../../public/static/viewer/img_page_viewer_left.gif';
import IMG_RIGHT from '../../../public/static/viewer/img_page_viewer_right.gif';
import PageSwiper, {IPageSwiper} from '../../common/component/PageSwiper';
import {KeyboardKeyType, REGION} from '../../common/constant';
import {Page} from '../../common/constant/page';
import {pToast} from '../../common/container/PToast';
import VideoClip from '../../common/container/VideoClip';
import {getKeyboardKeyType} from '../../common/function';
import useTypedSelector from '../../common/hook/useTypedSelector';
import {IVideoClip} from '../../common/type';
import {round} from '../../common/util/number';
import MaxWidth from '../../commonSub/component/MaxWidth';
import {sendViewerEvent, THRESHOLD_TO_MOVE_EPISODE} from '../common';
import ProgressBar from '../component/ProgressBar';
import ViewerBigBanner from '../component/viewerEnd/ViewerBigBanner';
import WaitForFreePlusBanner from '../component/viewerEnd/WaitForFreePlusBanner';
import ViewerImage from '../component/ViewerImage';
import {actions} from '../state/actions';
import {IViewDirection, IViewerProps} from '../type';

interface IProps extends IViewerProps {
  direction?: IViewDirection;
}

export default function PageViewer({
  contentId,
  episodeId,
  images,
  initialPage = 1,
  isRunMode,
  isStopSales,
  videoClip,
  direction = 'forward',
  onProgressChanged,
  onPageChanged,
  goToPrevEpisode,
  goToNextEpisode,
  prevPage,
}: IProps) {
  const dispatch = useDispatch();
  const [viewerState, eventBanner, extendBanner, title, badge] =
    useTypedSelector(({viewer}) => [
      viewer.viewerState,
      viewer.viewerEnd?.eventBanner,
      viewer.viewerEnd?.extendBanner,
      viewer.content?.title,
      viewer.content?.badge,
    ]);
  const swiperRef = useRef<IPageSwiper>(null);
  const showViewerEnd = !isRunMode && !isStopSales;
  const showEventBanner =
    showViewerEnd && eventBanner?.type === 'adNormalEvent';
  const showExtendBanner = showViewerEnd && REGION === 'kor' && extendBanner;
  const showBannerPage =
    showEventBanner || showExtendBanner || (title && badge?.waitfree);
  const totalPage = isRunMode
    ? images.length
    : images.length +
      (showViewerEnd ? 1 : 0) +
      (showBannerPage ? 1 : 0) +
      (videoClip ? 1 : 0);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const isRtl = direction === 'backward';
  const lastImageIndex =
    videoClip?.location === 'top' ? images.length : images.length - 1;
  const isEnd = currentPage >= totalPage;

  useEffect(() => {
    if (prevPage !== Page.Viewer) {
      pToast({
        toastId: 'view_direction',
        desc: t =>
          t(
            isRtl
              ? 'viewer_page_swipe_right_snackbar'
              : 'viewer_page_swipe_snackbar'
          ),
        variant: 'black',
        src: isRtl ? IMG_LEFT : IMG_RIGHT,
        showingDuration: 800,
      });
    }
  }, [isRtl, prevPage]);

  const isFirst = currentPage === 1;
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const keyType = getKeyboardKeyType(e);
      if (keyType === KeyboardKeyType.ArrowLeft) {
        if (isFirst && !isRtl) {
          goToPrevEpisode();
        } else if (isEnd && isRtl) {
          goToNextEpisode();
        } else {
          isRtl
            ? swiperRef.current?.slideNext()
            : swiperRef.current?.slidePrev();
        }
      } else if (keyType === KeyboardKeyType.ArrowRight) {
        if (isFirst && isRtl) {
          goToPrevEpisode();
        } else if (isEnd && !isRtl) {
          goToNextEpisode();
        } else {
          isRtl
            ? swiperRef.current?.slidePrev()
            : swiperRef.current?.slideNext();
        }
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [goToNextEpisode, goToPrevEpisode, isEnd, isFirst, isRtl]);

  useEffect(() => {
    if (isRunMode) {
      setCurrentPage(Math.min(totalPage, currentPage));
    }
  }, [currentPage, isRunMode, totalPage]);

  const customProps = {
    content_id: contentId,
    episode_id: episodeId,
    viewer_type: 'page',
  };
  const onSlideChangeTransitionStart = (swiper: IPageSwiper) => {
    if (swiper.realIndex === lastImageIndex) {
      sendViewerEvent({
        actionName: 'read',
        action: 'complete',
        customProps,
      });
    }
  };

  const isKeepViewerStateRef = useRef(false);
  const onSlideChangeTransitionEnd = (swiper: IPageSwiper) => {
    const page = swiper.realIndex + 1;
    setCurrentPage(page);
    onPageChanged(page);
    onProgressChanged?.(round(Math.min(page / images.length, 1), 0.1));

    if (isKeepViewerStateRef.current) {
      isKeepViewerStateRef.current = false;
    } else {
      if (page < totalPage) {
        dispatch(actions.setViewerState('none'));
      } else {
        !isRunMode && dispatch(actions.setViewerState('default'));
      }
    }
  };

  const onSlideResetTransitionEnd = (swiper: IPageSwiper) => {
    if (!isRunMode) return;

    const dist = Math.abs(swiper.touches.diff);
    if (dist < THRESHOLD_TO_MOVE_EPISODE) return;

    // 타입에 추가가 안되어 있다.
    const direction: 'prev' | 'next' = (swiper as any).swipeDirection;
    if (currentPage === 1 && direction === 'prev') {
      goToPrevEpisode();
    } else if (isEnd && direction === 'next') {
      goToNextEpisode();
    }
  };

  const moveToPage = (page: number) => {
    isKeepViewerStateRef.current = true;
    swiperRef.current?.slideTo(page - 1, 0);
  };

  const slides = [];
  if (videoClip?.location === 'top') {
    slides.push(<VideoSlide key={0} videoClip={videoClip} />);
  }
  const startImageIndex = slides.length;
  images.forEach((image, index) =>
    slides.push(<ViewerImage key={index + startImageIndex} src={image.url} />)
  );
  if (videoClip?.location === 'bottom') {
    slides.push(<VideoSlide key="videoClip" videoClip={videoClip} />);
  }
  if (showBannerPage) {
    slides.push(
      <div
        key="eventBanner"
        className={VIEWER_END_STYLE}
        style={{direction: 'ltr'}}
      >
        <MaxWidth hasMdWidth className="px-11">
          {showEventBanner && (
            <EventBanner
              contentId={contentId}
              episodeId={episodeId}
              banner={eventBanner}
            />
          )}
          {title && badge && (
            <WaitForFreePlusBanner
              title={title}
              badge={badge}
              hasBottomLine={!!showExtendBanner}
              className={cn(showExtendBanner && 'mb-20')}
            />
          )}
          {showExtendBanner && <ViewerBigBanner viewType="page" />}
        </MaxWidth>
      </div>
    );
  }
  if (showViewerEnd) {
    slides.push(
      <div
        key="viewerEnd"
        className={VIEWER_END_STYLE}
        style={{direction: 'ltr'}}
      >
        <ViewerEnd className="pb-84" goToNextEpisode={goToNextEpisode} />
      </div>
    );
  }

  return (
    <div className="w-full h-full">
      <PageSwiper
        ref={swiperRef}
        key={initialPage} // initialPage 변경시 반영되도록 key로 사용
        initialSlide={initialPage - 1}
        isContainerAutoHeight
        isFlipX={isRtl}
        onSlideChangeTransitionStart={onSlideChangeTransitionStart}
        onSlideChangeTransitionEnd={onSlideChangeTransitionEnd}
        onSlideResetTransitionEnd={onSlideResetTransitionEnd}
        touchStartPreventDefault={false}
      >
        {slides}
      </PageSwiper>
      <div
        className={cn(
          'fixed w-full bottom-98 left-0 right-0 m-auto px-20 z-1',
          'transition-opacity duration-300',
          'sm:p-0 sm:w-[335px]',
          {
            'opacity-0 pointer-events-none': viewerState !== 'default',
          }
        )}
      >
        <ProgressBar
          value={currentPage}
          maxValue={totalPage}
          isRtl={isRtl}
          onChange={value => moveToPage(Math.max(value, 1))}
        />
      </div>
    </div>
  );
}

const VideoSlide = ({videoClip}: {videoClip: IVideoClip}) => {
  return (
    <div className="w-full h-full bg-white flex-center ">
      <div className="w-full max-w-default-max-width">
        <VideoClip video={videoClip} isLazy={false} />
      </div>
    </div>
  );
};

const VIEWER_END_STYLE =
  'flex flex-col justify-center h-full overflow-y-auto no-scrollbar bg-background-02';
